import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { captureAcquisitionSession } from '../../services/acquisitionSession';
import { buildLoginRedirectOptions } from '../../utils/authRedirect';
import ErrorHandler from '../../utils/errorHandler';

const isPreviewAuthBypassEnabled = () =>
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_TRONA_PREVIEW_AUTH_BYPASS === 'true';

const AuthWrapper = ({ children }) => {
  const { isAuthenticated, isLoading, error, user, loginWithRedirect } =
    useAuth0();
  const location = useLocation();
  const { t } = useTranslation();
  const previewAuthBypass = isPreviewAuthBypassEnabled();

  // Sync Auth0 token with API service (tokens stored in memory, not localStorage)
  useAuthenticatedApi();

  // Set Sentry user context for error tracking
  useEffect(() => {
    if (isAuthenticated && user) {
      ErrorHandler.setUserContext(user);
    } else if (previewAuthBypass) {
      ErrorHandler.setUserContext({
        sub: 'trona-preview-user',
        email: 'preview@tronallc.com',
        name: 'TRONA Preview',
      });
    } else {
      ErrorHandler.clearUserContext();
    }
  }, [isAuthenticated, user, previewAuthBypass]);

  // Check if current path is an auth route (sign-in, sign-up, etc.)
  const isAuthRoute = location.pathname.startsWith('/auth');

  if (previewAuthBypass) {
    return children;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#fbfaff] dark:bg-neutral-950">
        <div className="text-center">
          {/* Logo */}
          <div className="mx-auto mb-8 inline-flex rounded-xl bg-white p-3 shadow-sm ring-1 ring-neutral-200">
            <img
              src="/brand/full-logo.png"
              alt="TRONA"
              className="mx-auto h-12 w-auto"
            />
          </div>

          {/* Spinner */}
          <div className="relative mb-6">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-neutral-200 border-t-secondary-500"></div>
          </div>

          {/* Text */}
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            {t('auth.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#fbfaff] dark:bg-neutral-950">
        <div className="mx-auto max-w-md p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-error-100 dark:bg-error-900/25">
              <svg
                className="h-8 w-8 text-error-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <h2 className="mb-2 text-xl font-semibold text-neutral-900 dark:text-white">
            {t('auth.authError')}
          </h2>
          <p className="mb-6 text-neutral-500 dark:text-neutral-400">
            {error.message}
          </p>
          <button
            onClick={() => loginWithRedirect(buildLoginRedirectOptions(location))}
            className="inline-flex items-center justify-center rounded-lg bg-secondary-500 px-6 py-3 font-medium text-white transition-colors hover:bg-secondary-600"
          >
            {t('auth.retry')}
          </button>
        </div>
      </div>
    );
  }

  // If not authenticated and NOT on an auth route, redirect directly to Auth0
  if (!isAuthenticated && !isAuthRoute) {
    captureAcquisitionSession({ location });
    loginWithRedirect(buildLoginRedirectOptions(location));
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#fbfaff] dark:bg-neutral-950">
        <div className="text-center">
          <div className="mx-auto mb-8 inline-flex rounded-xl bg-white p-3 shadow-sm ring-1 ring-neutral-200">
            <img
              src="/brand/full-logo.png"
              alt="TRONA"
              className="mx-auto h-12 w-auto"
            />
          </div>
          <div className="relative mb-6">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-neutral-200 border-t-secondary-500"></div>
          </div>
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            {t('auth.redirecting')}
          </p>
        </div>
      </div>
    );
  }

  // If not authenticated but ON an auth route, show the auth pages
  // If authenticated, show the children (protected routes)
  return children;
};

export default AuthWrapper;
